import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { makeAutoObservable, observable, ObservableMap } from 'mobx'
import { type RootStore } from 'stores/RootStore'
import { AlmanacFileSummary } from 'types'
import { HttpError } from 'utils/error'

type AlmanacFileId = string
type Url = string
type AlmanacFile = AlmanacFileSummary

export class AlmanacFilesStore {
  rootStore: RootStore
  files: ObservableMap<AlmanacFileId, AlmanacFile> = observable.map()
  requests: ObservableMap<Url, Promise<AlmanacFile>> = observable.map()

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  add = (file: AlmanacFile) => {
    this.files.set(file.id, file)
  }

  bulkAdd = (files: AlmanacFile[]) => {
    files.forEach(this.add)
  }

  get = (id: AlmanacFileId) => this.files.get(id)

  fetch = (id: AlmanacFileId, force = false) => {
    return new Promise<AlmanacFile>((resolve, reject) => {
      const file = this.files.get(id)
      if (file && !force) {
        resolve(file)
        return
      }
      const url = `/almanac_files/${id}`

      let request = this.requests.get(url)
      if (!request) {
        request = sendToApi<{ almanac_file: AlmanacFile }>(url, {
          params: { include: ['version_control_mode.metadata'] },
        }).then((res) => {
          if (res.type !== SUCCESS_RESPONSE_TYPE) {
            if ('status' in res && 'statusText' in res) {
              throw new HttpError(res.statusText, res.status)
            }
            throw new Error()
          }
          this.files.set(id, res.data.almanac_file)
          return res.data.almanac_file
        })
        this.requests.set(url, request)
      }
      request
        .then(resolve)
        .catch(reject)
        .finally(() => this.requests.delete(url))
    })
  }

  updateAttrs = (id: AlmanacFileId, attributes: Partial<AlmanacFile>) => {
    const file = this.get(id)
    if (!file) return
    Object.assign(file, attributes)
  }
}
